import React, { useState, useEffect, useRef } from 'react';
import { FaEnvelope, FaUserCircle, FaPaperPlane, FaUsers, FaSearch, FaFilter, FaDownload, FaTrash, FaPlus } from 'react-icons/fa';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import LoadingSpinner from '../common/LoadingSpinner';
import Modal from '../common/Modal';
import { formatDate } from '../../utils/helpers';

/**
 * School messaging component
 * Allows school to communicate with parents, teachers, and staff
 */
const SchoolMessaging = () => {
  const { showSuccess, showError } = useToast();
  const messageInputRef = useRef(null);

  // Message state
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const messagesEndRef = useRef(null);
  
  // New message modal
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [composingMessage, setComposingMessage] = useState({
    subject: '',
    message: '',
    recipientType: 'individual', // 'individual', 'class', 'all-parents', 'all-teachers'
    recipientId: ''
  });
  
  // Filters state
  const [filters, setFilters] = useState({
    type: 'all', // 'all', 'parents', 'teachers', 'staff'
    status: 'all', // 'all', 'unread', 'read'
    date: 'all' // 'all', 'today', 'week', 'month'
  });
  
  // Recipients data
  const [recipients, setRecipients] = useState({
    teachers: [],
    parents: [],
    classes: []
  });
  
  // Fetch conversations and recipients on component mount
  useEffect(() => {
    fetchConversations();
    fetchRecipients();
  }, [filters]);

  // Scroll to bottom of messages when conversation changes or new message added
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [currentConversation, messages]);

  // Fetch conversations from API
  const fetchConversations = async () => {
    try {
      setLoading(true);
      
      // Prepare query params
      const params = new URLSearchParams();
      if (filters.type !== 'all') params.append('type', filters.type);
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.date !== 'all') params.append('date', filters.date);
      if (searchTerm) params.append('search', searchTerm);
      
      const queryString = params.toString();
      const endpoint = `/schools/messaging/conversations${queryString ? `?${queryString}` : ''}`;
      
      const response = await api.get(endpoint);
      setMessages(response.data.data || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      showError('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  // Fetch available recipients
  const fetchRecipients = async () => {
    try {
      const response = await api.get('/schools/messaging/recipients');
      setRecipients(response.data.data || {
        teachers: [],
        parents: [],
        classes: []
      });
    } catch (error) {
      console.error('Error fetching recipients:', error);
      showError('Failed to load recipients');
    }
  };

  // Select a conversation and fetch messages
  const selectConversation = async (conversation) => {
    if (currentConversation?._id === conversation._id) return;
    
    try {
      setCurrentConversation(conversation);
      setSelectedRecipient(conversation.recipient);
      
      // Mark as read if unread
      if (conversation.unreadCount > 0) {
        await api.put(`/schools/messaging/conversations/${conversation._id}/read`);
        
        // Update the messages list to show this conversation as read
        setMessages(prev => 
          prev.map(msg => 
            msg._id === conversation._id 
              ? { ...msg, unreadCount: 0 } 
              : msg
          )
        );
      }
      
      // Fetch conversation messages
      const response = await api.get(`/schools/messaging/conversations/${conversation._id}/messages`);
      conversation.messages = response.data.data || [];
      
      // Update the current conversation with messages
      setCurrentConversation({...conversation});
      
      // Focus message input
      if (messageInputRef.current) {
        messageInputRef.current.focus();
      }
    } catch (error) {
      console.error('Error fetching conversation messages:', error);
      showError('Failed to load messages');
    }
  };

  // Handle sending message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || !currentConversation) return;
    
    try {
      const response = await api.post(`/schools/messaging/conversations/${currentConversation._id}/messages`, {
        content: newMessage.trim()
      });
      
      // Update the current conversation with the new message
      const updatedMessages = [...(currentConversation.messages || []), response.data.data];
      setCurrentConversation(prev => ({
        ...prev,
        messages: updatedMessages,
        lastMessage: newMessage.trim(),
        lastMessageDate: new Date().toISOString()
      }));
      
      // Update the messages list
      setMessages(prev => 
        prev.map(msg => 
          msg._id === currentConversation._id 
            ? {
                ...msg,
                lastMessage: newMessage.trim(),
                lastMessageDate: new Date().toISOString()
              } 
            : msg
        )
      );
      
      // Clear input
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      showError('Failed to send message');
    }
  };

  // Handle new message composition
  const handleComposeChange = (e) => {
    const { name, value } = e.target;
    setComposingMessage(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle starting a new conversation
  const handleStartConversation = async () => {
    if (!composingMessage.message.trim() || !composingMessage.subject.trim()) {
      showError('Subject and message are required');
      return;
    }
    
    if (composingMessage.recipientType === 'individual' && !composingMessage.recipientId) {
      showError('Please select a recipient');
      return;
    }
    
    try {
      const payload = {
        subject: composingMessage.subject.trim(),
        message: composingMessage.message.trim(),
        recipientType: composingMessage.recipientType
      };
      
      if (composingMessage.recipientType === 'individual') {
        payload.recipientId = composingMessage.recipientId;
      }
      
      const response = await api.post('/schools/messaging/conversations', payload);
      
      showSuccess('Message sent successfully');
      setShowNewMessageModal(false);
      
      // Reset compose form
      setComposingMessage({
        subject: '',
        message: '',
        recipientType: 'individual',
        recipientId: ''
      });
      
      // Add new conversation to the list and select it
      const newConversation = response.data.data;
      setMessages(prev => [newConversation, ...prev]);
      selectConversation(newConversation);
    } catch (error) {
      console.error('Error starting conversation:', error);
      showError('Failed to send message');
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle search submit
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchConversations();
  };

  // Handle filter change
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Get recipient options based on type
  const getRecipientOptions = () => {
    switch (composingMessage.recipientType) {
      case 'individual':
        const allRecipients = [
          ...recipients.teachers.map(t => ({ ...t, type: 'Teacher' })),
          ...recipients.parents.map(p => ({ ...p, type: 'Parent' }))
        ];
        return allRecipients;
      case 'class':
        return recipients.classes;
      default:
        return [];
    }
  };

  // Get recipient display name
  const getRecipientName = (recipient) => {
    if (!recipient) return '';
    if (recipient.type === 'Class') return `${recipient.name} (Class)`;
    return `${recipient.name} (${recipient.type})`;
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex flex-col md:flex-row justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <FaEnvelope className="mr-2 text-primary" /> School Messaging
          </h1>
          <p className="text-gray-600 mt-1">
            Communicate with parents, teachers, and staff
          </p>
        </div>
        
        <div className="mt-4 md:mt-0">
          <button
            onClick={() => setShowNewMessageModal(true)}
            className="btn-primary flex items-center"
          >
            <FaPlus className="mr-2" /> New Message
          </button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Conversations Sidebar */}
        <div className="bg-white rounded-lg shadow-soft overflow-hidden md:col-span-1">
          <div className="border-b border-gray-200 p-4 sticky top-0 bg-white z-10">
            {/* Search */}
            <form onSubmit={handleSearchSubmit} className="mb-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search conversations..."
                  className="form-input pl-10 w-full"
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaSearch className="text-gray-400" />
                </div>
                <button
                  type="submit"
                  className="absolute inset-y-0 right-0 px-3 flex items-center bg-primary text-white rounded-r-md"
                >
                  Search
                </button>
              </div>
            </form>
            
            {/* Filters */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <select
                  name="type"
                  value={filters.type}
                  onChange={handleFilterChange}
                  className="form-select w-full text-sm"
                >
                  <option value="all">All Recipients</option>
                  <option value="parents">Parents</option>
                  <option value="teachers">Teachers</option>
                  <option value="staff">Staff</option>
                </select>
              </div>
              <div>
                <select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  className="form-select w-full text-sm"
                >
                  <option value="all">All Messages</option>
                  <option value="unread">Unread</option>
                  <option value="read">Read</option>
                </select>
              </div>
            </div>
          </div>
          
          {/* Conversations List */}
          <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 240px)' }}>
            {loading ? (
              <div className="p-8 flex justify-center">
                <LoadingSpinner size="md" />
              </div>
            ) : messages.length === 0 ? (
              <div className="p-8 text-center">
                <FaEnvelope className="mx-auto text-gray-300 text-5xl mb-4" />
                <h3 className="text-lg font-medium text-gray-600 mb-2">No Messages</h3>
                <p className="text-gray-500 mb-4">
                  {searchTerm ? 'No conversations match your search' : 'Start a new conversation'}
                </p>
                <button
                  onClick={() => setShowNewMessageModal(true)}
                  className="btn-primary flex items-center mx-auto"
                >
                  <FaPlus className="mr-2" /> New Message
                </button>
              </div>
            ) : (
              <div>
                {messages.map(conversation => (
                  <div
                    key={conversation._id}
                    className={`border-b border-gray-200 p-4 cursor-pointer transition-colors hover:bg-gray-50 ${
                      currentConversation?._id === conversation._id ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => selectConversation(conversation)}
                  >
                    <div className="flex justify-between mb-1">
                      <div className="font-medium text-gray-900 flex items-center">
                        {conversation.recipient.name}
                        {conversation.unreadCount > 0 && (
                          <span className="ml-2 bg-primary text-white text-xs rounded-full px-2 py-1">
                            {conversation.unreadCount}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatDate(conversation.lastMessageDate)}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">
                      {conversation.recipient.type}
                    </div>
                    <div className="mt-1 text-sm text-gray-600 truncate">
                      {conversation.subject}
                    </div>
                    <div className="mt-1 text-xs text-gray-500 truncate">
                      {conversation.lastMessage}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Conversation Detail */}
        <div className="bg-white rounded-lg shadow-soft overflow-hidden md:col-span-2 flex flex-col min-h-[500px]">
          {currentConversation ? (
            <>
              {/* Header */}
              <div className="border-b border-gray-200 p-4 flex justify-between items-center">
                <div>
                  <h2 className="font-medium text-lg">{currentConversation.subject}</h2>
                  <div className="text-sm text-gray-600 flex items-center">
                    <span className="mr-2">To: {getRecipientName(currentConversation.recipient)}</span>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button className="btn-icon text-gray-400 hover:text-primary">
                    <FaDownload title="Download conversation" />
                  </button>
                  <button className="btn-icon text-gray-400 hover:text-red-500">
                    <FaTrash title="Delete conversation" />
                  </button>
                </div>
              </div>
              
              {/* Messages */}
              <div 
                className="flex-1 p-4 space-y-4 overflow-y-auto"
                style={{ maxHeight: 'calc(100vh - 280px)' }}
              >
                {currentConversation.messages?.map((message, index) => (
                  <div 
                    key={index} 
                    className={`flex ${message.fromSchool ? 'justify-end' : 'justify-start'}`}
                  >
                    <div 
                      className={`max-w-[80%] rounded-lg p-3 ${
                        message.fromSchool 
                          ? 'bg-primary text-white rounded-br-none'
                          : 'bg-gray-100 text-gray-800 rounded-bl-none'
                      }`}
                    >
                      <div className="text-sm">{message.content}</div>
                      <div className={`text-xs mt-1 ${message.fromSchool ? 'text-blue-100' : 'text-gray-500'}`}>
                        {formatDate(message.timestamp)}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
              
              {/* Message Input */}
              <div className="border-t border-gray-200 p-4">
                <form onSubmit={handleSendMessage} className="flex items-end">
                  <div className="flex-1 mr-2">
                    <textarea
                      ref={messageInputRef}
                      className="form-textarea w-full"
                      placeholder="Type your message..."
                      rows="3"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    className="btn-primary h-10 w-10 flex items-center justify-center"
                    disabled={!newMessage.trim()}
                  >
                    <FaPaperPlane />
                  </button>
                </form>
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center p-8">
              <FaEnvelope className="text-gray-300 text-6xl mb-4" />
              <h3 className="text-xl font-medium text-gray-600 mb-2">No Conversation Selected</h3>
              <p className="text-gray-500 text-center mb-6 max-w-md">
                Select a conversation from the list or start a new one to begin messaging
              </p>
              <button
                onClick={() => setShowNewMessageModal(true)}
                className="btn-primary flex items-center"
              >
                <FaPlus className="mr-2" /> New Message
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* New Message Modal */}
      <Modal
        isOpen={showNewMessageModal}
        onClose={() => setShowNewMessageModal(false)}
        title="New Message"
        size="lg"
      >
        <div className="p-6">
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Recipient Type</label>
            <select
              name="recipientType"
              value={composingMessage.recipientType}
              onChange={handleComposeChange}
              className="form-select w-full"
            >
              <option value="individual">Individual</option>
              <option value="class">Class</option>
              <option value="all-parents">All Parents</option>
              <option value="all-teachers">All Teachers</option>
            </select>
          </div>
          
          {composingMessage.recipientType === 'individual' || composingMessage.recipientType === 'class' ? (
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">
                Select {composingMessage.recipientType === 'class' ? 'Class' : 'Recipient'}
              </label>
              <select
                name="recipientId"
                value={composingMessage.recipientId}
                onChange={handleComposeChange}
                className="form-select w-full"
              >
                <option value="">-- Select --</option>
                {getRecipientOptions().map(recipient => (
                  <option key={recipient._id} value={recipient._id}>
                    {recipient.name} {recipient.type ? `(${recipient.type})` : ''}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="mb-4 p-3 bg-blue-50 text-blue-700 rounded-md">
              <div className="flex items-center mb-1">
                <FaUsers className="mr-2" />
                <span className="font-medium">
                  {composingMessage.recipientType === 'all-parents' ? 'All Parents' : 'All Teachers'}
                </span>
              </div>
              <p className="text-sm">
                Your message will be sent to {' '}
                {composingMessage.recipientType === 'all-parents' ? 'all parents' : 'all teachers'} 
                {' '} at your school.
              </p>
            </div>
          )}
          
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Subject</label>
            <input
              type="text"
              name="subject"
              value={composingMessage.subject}
              onChange={handleComposeChange}
              className="form-input w-full"
              placeholder="Enter message subject"
            />
          </div>
          
          <div className="mb-6">
            <label className="block text-sm font-medium mb-1">Message</label>
            <textarea
              name="message"
              value={composingMessage.message}
              onChange={handleComposeChange}
              className="form-textarea w-full"
              rows="6"
              placeholder="Type your message here..."
            ></textarea>
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => setShowNewMessageModal(false)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleStartConversation}
              className="btn-primary"
            >
              Send Message
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SchoolMessaging; 